import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Alert, Grid, Spin } from 'antd';
import { StarOutlined, FolderOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import NewsList from '../components/News/NewsList';
import TagGroupService, { TagGroup } from '../service/tagGroupService';
import '../styles/NewsDetail.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PersonalizedNewsPage: React.FC = () => {
    const { token } = useAuth();
    const { groupId } = useParams<{ groupId: string }>();
    const screens = useBreakpoint();
    const [tagGroup, setTagGroup] = useState<TagGroup | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const fetchTagGroup = useCallback(async () => {
        if (!groupId) return;

        setLoading(true);
        try {
            const group = await TagGroupService.getTagGroupById(parseInt(groupId));
            setTagGroup(group);
        } catch (error) {
            setTagGroup(null);
        } finally {
            setLoading(false);
        }
    }, [groupId]);

    useEffect(() => {
        if (groupId && token) {
            fetchTagGroup();
        } else if (!groupId) {
            // Clear tag group state when no groupId
            setTagGroup(null);
        }
    }, [groupId, token, fetchTagGroup]);

    if (!token) {
        return (
            <div style={{ padding: screens.md ? '24px' : '16px', textAlign: 'center' }}>
                <Alert
                    message="Authentication Required"
                    description="Please log in to view personalized news."
                    type="info"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: screens.md ? '5px' : '0' }}>
            <div style={{
                textAlign: 'center',
                marginBottom: screens.md ? '24px' : '16px',
                padding: screens.md ? '16px' : '12px'
            }}>
                <Title level={screens.md ? 2 : 3} style={{ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    {groupId && tagGroup ? (
                        <>
                            <FolderOutlined style={{ color: '#1890ff' }} />
                            {loading ? <Spin size="small" /> : tagGroup.name}
                        </>
                    ) : groupId && loading ? (
                        <>
                            <FolderOutlined style={{ color: '#1890ff' }} />
                            <Spin size="small" />
                        </>
                    ) : (
                        <>
                            <StarOutlined style={{ color: '#1890ff' }} />
                            Personalized News
                        </>
                    )}
                </Title>
                <Text type="secondary" style={{ fontSize: screens.md ? '16px' : '14px' }}>
                    {groupId && tagGroup
                        ? `News from your ${tagGroup.name} tag group`
                        : groupId && loading
                        ? 'Loading group information...'
                        : 'Articles tailored to your preferences'
                    }
                </Text>
            </div>

            <NewsList
                usePersonalizedNews={true}
                groupId={groupId ? parseInt(groupId) : undefined}
            />
        </div>
    );
};

export default PersonalizedNewsPage;